import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import ReportCard from "./ReportCard";
import { IMergedReportCategory, IMergedReportEntry } from "pages/secure/shared/reports/reportCatalog.types";

interface IReportsCategorySectionProps {
  category: IMergedReportCategory;
  expanded: boolean;
  embedded?: boolean;
  setSectionRef?: (element: HTMLElement | null) => void;
  onToggleExpanded: () => void;
  onOpenReport: (report: IMergedReportEntry) => void;
  onTogglePin: (code: string) => void;
  isPinned: (code: string) => boolean;
  canSchedule: boolean;
  onScheduleReport: (report: IMergedReportEntry) => void;
  onQuickExport: (report: IMergedReportEntry) => void;
  onRunReport: (report: IMergedReportEntry) => void;
}

export default function ReportsCategorySection(props: IReportsCategorySectionProps) {
  const {
    category,
    expanded,
    embedded = false,
    setSectionRef,
    onToggleExpanded,
    onOpenReport,
    onTogglePin,
    isPinned,
    canSchedule,
    onScheduleReport,
    onQuickExport,
    onRunReport,
  } = props;

  const { t } = useTranslation();
  const { id, title, icon, reports } = category;
  const panelId = `reports-panel-${id}`;
  const headingId = `reports-heading-${id}`;
  const sectionClassName = "reports-list-section reports-list-section--no-summary";

  const handleCollapsedKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onToggleExpanded();
    }
  };

  const headerContent = (
    <>
      <span className="reports-list-section__icon" aria-hidden="true">
        <FontAwesomeIcon icon={icon} />
      </span>
      <div className="reports-list-section__heading">
        <div className="reports-list-section__heading-text">
          <h2 id={headingId} className="reports-list-section__title">
            {t(title)}
          </h2>
        </div>
        <span className="reports-list-section__toggle" aria-hidden="true">
          <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
        </span>
      </div>
    </>
  );

  const content = expanded ? (
    <>
      <button
        type="button"
        className="reports-list-section__header"
        aria-expanded
        aria-controls={panelId}
        onClick={onToggleExpanded}
      >
        {headerContent}
      </button>
      <div id={panelId} className="reports-list-section__grid" role="list">
        {reports.map(report => (
          <div key={report.code} role="listitem">
            <ReportCard
              report={report}
              isPinned={isPinned(report.code)}
              canSchedule={canSchedule}
              onOpen={() => onOpenReport(report)}
              onTogglePin={() => onTogglePin(report.code)}
              onScheduleReport={() => onScheduleReport(report)}
              onQuickExport={() => onQuickExport(report)}
              onRunReport={() => onRunReport(report)}
            />
          </div>
        ))}
      </div>
    </>
  ) : (
    <div className="reports-list-section__header">{headerContent}</div>
  );

  const wrapperClassName = `${sectionClassName}${expanded ? "" : " reports-list-section--collapsed"}`;
  const collapsedProps = expanded
    ? {}
    : {
        role: "button" as const,
        tabIndex: 0,
        "aria-expanded": false,
        "aria-controls": panelId,
        onClick: onToggleExpanded,
        onKeyDown: handleCollapsedKeyDown,
      };

  if (embedded) {
    return (
      <div className={wrapperClassName} {...collapsedProps}>
        {content}
      </div>
    );
  }

  return (
    <section
      ref={setSectionRef}
      id={id}
      className={wrapperClassName}
      role={expanded ? "tabpanel" : undefined}
      aria-labelledby={expanded ? `reports-tab-${id}` : undefined}
      {...collapsedProps}
    >
      {content}
    </section>
  );
}
